import { slaFor } from "../components/RequestRouting";
import type { RoutingStep } from "../components/RequestRouting";

export type HelpDeskQueue =
  | "IT"
  | "HR"
  | "Finance"
  | "Facilities"
  | "Security"
  | "Contracts";

export type HelpDeskTier = 1 | 2;

export type RouteHopKind = "assigned" | "reroute" | "escalate" | "accept" | "resolve";

export type RouteHop = {
  at: string;
  kind: RouteHopKind;
  from_name: string;
  to_name: string;
  reason?: string;
};

export type HelpDeskTicket = {
  id: string;
  subject: string;
  body: string;
  category: HelpDeskQueue;
  status: "Submitted" | "In progress" | "Resolved" | "Closed";
  tier: HelpDeskTier;
  requester_id: string;
  requester_email: string;
  requester_name: string;
  assignee_id: string;
  assignee_name: string;
  project_id?: string;
  project_code?: string;
  opened_at: string;
  accepted_at?: string;
  resolved_at?: string;
  resolve_note?: string;
  route_history: RouteHop[];
};

export type DomainOwner = { employee_id: string; name: string };

export const HELP_DESK_QUEUES: HelpDeskQueue[] = [
  "IT",
  "HR",
  "Finance",
  "Facilities",
  "Security",
  "Contracts",
];

/** Named Tier-1 domain owners (no central triage). */
export const DOMAIN_OWNERS: Record<HelpDeskQueue, DomainOwner> = {
  IT: { employee_id: "emp-ortiz", name: "Jordan Ortiz" },
  HR: { employee_id: "emp-blake", name: "Morgan Blake" },
  Finance: { employee_id: "emp-blake", name: "Morgan Blake" },
  Facilities: { employee_id: "emp-4", name: "Riley Nguyen" },
  Security: { employee_id: "emp-ortiz", name: "Jordan Ortiz" },
  Contracts: { employee_id: "emp-patel", name: "Sam Patel" },
};

export const GOVSUITE_SUPPORT = {
  employee_id: "govsuite-support",
  name: "GovSuite Support",
  email_domain: "@govsuite.com",
};

export function isGovSuiteEmail(email: string | null | undefined): boolean {
  return (email || "").toLowerCase().trim().endsWith("@govsuite.com");
}

export type FaqArticle = {
  id: string;
  title: string;
  body: string;
  tags: string[];
};

export const HELP_FAQS: FaqArticle[] = [
  {
    id: "faq-1",
    title: "How do I change my home address?",
    body: "Open My profile, request an address change. Your supervisor approves first, then HR processes the update.",
    tags: ["address", "home", "move", "hr", "profile"],
  },
  {
    id: "faq-2",
    title: "How do I update my W-4?",
    body: "Open Payroll, review your latest W-4, then choose Update W-4. That opens a help desk ticket routed to HR.",
    tags: ["w-4", "w4", "tax", "withholding", "payroll", "hr"],
  },
  {
    id: "faq-3",
    title: "VPN or laptop issues",
    body: "Open Help desk for IT problems. Tickets go to your company's IT domain owner (not a central triage desk).",
    tags: ["vpn", "laptop", "password", "mfa", "it", "computer"],
  },
  {
    id: "faq-4",
    title: "Project finance questions",
    body: "Ask Help desk about a project's finances and pick the project. The ticket routes to that project's finance assignee.",
    tags: ["finance", "budget", "funding", "project", "burn", "invoice"],
  },
  {
    id: "faq-5",
    title: "Product bug in GovSuite",
    body: "Open a company help ticket first. Your domain owner can escalate technical product issues to GovSuite Support (Tier 2).",
    tags: ["bug", "error", "govsuite", "product", "escalate", "tier"],
  },
  {
    id: "faq-6",
    title: "Leave vs payroll",
    body: "Leave is for balances and time-off requests. Payroll is for salary, bonuses, and tax forms.",
    tags: ["leave", "pto", "payroll", "salary"],
  },
];

type PhraseRule = { queue: HelpDeskQueue; phrases: string[]; weight: number };

const PHRASE_RULES: PhraseRule[] = [
  { queue: "HR", phrases: ["address change", "home address", "change address", "move", "mailing address"], weight: 5 },
  { queue: "HR", phrases: ["w-4", "w4", "withholding", "tax form", "benefits", "onboarding", "offboarding", "paystub", "handbook"], weight: 3 },
  { queue: "HR", phrases: ["address", "salary", "raise", "title change", "manager change"], weight: 2 },
  { queue: "IT", phrases: ["laptop", "password", "vpn", "email", "software", "printer", "network", "login", "mfa", "computer", "bug", "error", "govsuite"], weight: 2 },
  {
    queue: "Finance",
    phrases: [
      "invoice",
      "expense",
      "reimburse",
      "payment",
      "gl",
      "budget",
      "ap",
      "ar",
      "project finance",
      "funding",
      "burn rate",
      "eac",
      "project cost",
    ],
    weight: 2,
  },
  { queue: "Facilities", phrases: ["badge", "desk", "office", "parking", "hvac", "building", "keys", "space"], weight: 2 },
  { queue: "Security", phrases: ["clearance", "phishing", "incident", "cui", "fso", "badge lost", "security access"], weight: 2 },
  { queue: "Contracts", phrases: ["clin", "subcontract", "flowdown", "nda", "proposal", "rfp", "compliance"], weight: 2 },
];

export function suggestHelpDeskQueue(text: string): HelpDeskQueue {
  const lower = text.toLowerCase();
  const scores: Record<HelpDeskQueue, number> = {
    IT: 0,
    HR: 0,
    Finance: 0,
    Facilities: 0,
    Security: 0,
    Contracts: 0,
  };
  for (const rule of PHRASE_RULES) {
    for (const p of rule.phrases) {
      if (lower.includes(p)) scores[rule.queue] += rule.weight;
    }
  }
  let best: HelpDeskQueue = "IT";
  let bestScore = -1;
  for (const q of HELP_DESK_QUEUES) {
    if (scores[q] > bestScore) {
      bestScore = scores[q];
      best = q;
    }
  }
  return bestScore > 0 ? best : "IT";
}

export function isProjectFinanceTopic(text: string, category: HelpDeskQueue): boolean {
  if (category !== "Finance") return false;
  const lower = text.toLowerCase();
  return (
    lower.includes("project") ||
    lower.includes("funding") ||
    lower.includes("burn") ||
    lower.includes("eac") ||
    lower.includes("budget") ||
    lower.includes("clin")
  );
}

export type ProjectFinanceRef = {
  id: string;
  code: string;
  finance_assignee_employee_id: string;
  finance_assignee_name: string;
};

export function resolveAssignee(
  category: HelpDeskQueue,
  text: string,
  project?: ProjectFinanceRef | null,
): DomainOwner & { project_id?: string; project_code?: string } {
  if (project && isProjectFinanceTopic(text, category)) {
    return {
      employee_id: project.finance_assignee_employee_id,
      name: project.finance_assignee_name,
      project_id: project.id,
      project_code: project.code,
    };
  }
  const owner = DOMAIN_OWNERS[category];
  return { ...owner };
}

export function rankFaqs(text: string): FaqArticle[] {
  const lower = text.toLowerCase();
  const words = lower.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
  return [...HELP_FAQS]
    .map((a) => {
      let score = 0;
      for (const t of a.tags) if (lower.includes(t)) score += 3;
      for (const w of words) {
        if (a.title.toLowerCase().includes(w)) score += 2;
        if (a.body.toLowerCase().includes(w)) score += 1;
      }
      return { a, score };
    })
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score)
    .map((x) => x.a);
}

export function hoursAgo(h: number): string {
  return new Date(Date.now() - h * 3600_000).toISOString();
}

function buildSlaHistory(): Record<string, number[]> {
  const now = Date.now();
  const out: Record<string, number[]> = {};
  const defs: { key: string; base: number; n: number }[] = [
    { key: "HR|Accept", base: 6, n: 40 },
    { key: "HR|Resolve", base: 28, n: 40 },
    { key: "IT|Accept", base: 4, n: 50 },
    { key: "IT|Resolve", base: 18, n: 50 },
    { key: "Finance|Accept", base: 8, n: 30 },
    { key: "Finance|Resolve", base: 36, n: 30 },
    { key: "Facilities|Accept", base: 10, n: 25 },
    { key: "Facilities|Resolve", base: 48, n: 25 },
    { key: "Security|Accept", base: 5, n: 20 },
    { key: "Security|Resolve", base: 40, n: 20 },
    { key: "Contracts|Accept", base: 12, n: 20 },
    { key: "Contracts|Resolve", base: 60, n: 20 },
    { key: "Supervisor|Approve address", base: 10, n: 35 },
    { key: "HR|Process address", base: 24, n: 35 },
    { key: "Owner|Approve charge code", base: 16, n: 40 },
  ];
  for (const d of defs) {
    const samples: number[] = [];
    for (let i = 0; i < d.n; i++) {
      const dayOffset = (i * 57) % 60;
      void (now - dayOffset * 86400000);
      const jitter = ((i * 17) % 10) - 4;
      samples.push(Math.max(0.5, d.base + jitter));
    }
    out[d.key] = samples;
  }
  return out;
}

const SLA_HISTORY = buildSlaHistory();

export function slaForQueueStep(queue: string, step: string) {
  const samples = SLA_HISTORY[`${queue}|${step}`] ?? [];
  return slaFor(samples);
}

export function ticketRoutingSteps(t: HelpDeskTicket): RoutingStep[] {
  const hops = t.route_history;
  const steps: RoutingStep[] = [];
  let i = 0;
  for (const hop of hops) {
    i += 1;
    const title =
      hop.kind === "assigned"
        ? "Assigned"
        : hop.kind === "reroute"
          ? "Re-routed"
          : hop.kind === "escalate"
            ? "Escalated to Tier 2"
            : hop.kind === "accept"
              ? "Accepted"
              : "Resolved";
    steps.push({
      id: String(i),
      title,
      role: hop.to_name,
      status: "completed",
      at: hop.at,
      note: hop.reason || `${hop.from_name} → ${hop.to_name}`,
    });
  }
  if (t.status === "Resolved" || t.status === "Closed") {
    return steps;
  }
  if (t.status === "Submitted") {
    steps.push({
      id: String(i + 1),
      title: "Awaiting acceptance",
      role: t.assignee_name,
      status: "current",
      note: t.tier === 2 ? "Tier 2 — GovSuite Support" : `Tier 1 · ${t.category}`,
    });
    steps.push({
      id: String(i + 2),
      title: "Resolve",
      role: t.assignee_name,
      status: "upcoming",
    });
    return steps;
  }
  // In progress
  steps.push({
    id: String(i + 1),
    title: "In progress",
    role: t.assignee_name,
    status: "current",
    note: t.tier === 2 ? "Tier 2 — GovSuite Support" : `Tier 1 · ${t.category}`,
  });
  steps.push({
    id: String(i + 2),
    title: "Resolve",
    role: t.assignee_name,
    status: "upcoming",
  });
  return steps;
}

const TICKET_KEY = "govsuite.demo.helpdesk_tickets.v2";

export function loadHelpDeskTickets(seed: HelpDeskTicket[]): HelpDeskTicket[] {
  try {
    const raw = localStorage.getItem(TICKET_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as HelpDeskTicket[];
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {
    /* ignore */
  }
  return seed.map((t) => ({ ...t, route_history: [...t.route_history] }));
}

export function saveHelpDeskTickets(tickets: HelpDeskTicket[]) {
  localStorage.setItem(TICKET_KEY, JSON.stringify(tickets));
}
